/**
 * Deep link dos controles da Live Activity — a ponte entre o toque na Ilha e a
 * sessão de Foco.
 *
 * ── Por que Link, e não App Intent ──────────────────────────────────────────
 *
 * A decisão do fundador (23/08) foi a opção (c): **o botão ABRE o app já
 * agindo**. As alternativas mexiam em economia — fila nativa entrega a pausa
 * com atraso (e `pausedAt` é carimbado no servidor, então uma pausa "feita" às
 * 14:03 e entregue às 14:40 creditaria 37 min que não existiram), e chamar a
 * callable pelo Swift criaria um SEGUNDO dono de um caminho de economia, que é
 * a doença do BUG-81/87 na parte mais sensível do produto.
 *
 * O ganho de tabela da escolha, medido: `Link` funciona em Live Activity desde
 * **iOS 16.2**, enquanto `Button(intent:)` exige 17.0. Ou seja, o controle
 * alcança todo mundo que já vê a Ilha, sem ramo de disponibilidade e sem o
 * intent precisar existir nos dois targets.
 *
 * ── A ação atravessa o app pelo caminho NORMAL ──────────────────────────────
 *
 * O link só diz o que a pessoa pediu. Quem executa é o `useFocusTimer` de
 * sempre, dentro do modal — o mesmo par otimista + confirmação do servidor de
 * quando o gesto vem da tela. Não existe segundo caminho de pausa.
 *
 * ── O sessionId no link não é enfeite ───────────────────────────────────────
 *
 * Um card obsoleto (sessão anterior que ficou na tela, iOS que demorou a
 * dispensar) mandaria uma ação para a sessão ERRADA. O id viaja e o consumidor
 * descarta o que não é da sessão viva.
 */

/** Esquema declarado em `CFBundleURLTypes` (Info.plist do app iOS). */
export const FOCUS_URL_SCHEME = "livra";

export type FocusDeepLinkAction = "pause" | "resume" | "end";

export interface FocusDeepLinkCommand {
  action: FocusDeepLinkAction;
  /** `null` quando o link veio sem `s` (link antigo) — o consumidor decide. */
  sessionId: string | null;
}

/** Verbo na URL ↔ ação. Em português, como as rotas do app. */
const VERBOS: Record<string, FocusDeepLinkAction> = {
  pausar: "pause",
  retomar: "resume",
  encerrar: "end",
};

const VERBO_DE: Record<FocusDeepLinkAction, string> = {
  pause: "pausar",
  resume: "retomar",
  end: "encerrar",
};

/**
 * URL que vai no card. Montada aqui e não no layout para que o par
 * verbo↔ação tenha UM dono — o widget só recebe a string pronta.
 */
export function buildFocusActionUrl(action: FocusDeepLinkAction, sessionId: string): string {
  return `${FOCUS_URL_SCHEME}://foco/${VERBO_DE[action]}?s=${encodeURIComponent(sessionId)}`;
}

/**
 * Lê a URL que o sistema entregou ao app. `null` = não é nossa (o listener de
 * `appUrlOpen` recebe TODA URL que abre o app, inclusive as do OAuth).
 *
 * ⚠️ Regex, não `new URL`: esquema não-especial tem comportamento que varia
 * entre motores (WKWebView × jsdom × Node) justamente na parte que interessa
 * aqui, que é onde o host termina e o path começa. Três linhas de padrão não
 * dependem disso.
 */
/* ── Decisão: o que fazer com o pedido que chegou ────────────────────────── */

export type FocusActionOutcome =
  /** A sessão ainda não assentou — o pedido FICA pendente. */
  | { kind: "wait" }
  /** Consome o pedido e não faz nada (card obsoleto, ou já no estado pedido). */
  | { kind: "discard" }
  | { kind: "apply"; action: FocusDeepLinkAction };

/**
 * Extraída do efeito de propósito: dentro dele não havia como exercitar as três
 * guardas sem montar o modal inteiro, que é o mesmo motivo pelo qual
 * `resolvePauseTransition` saiu da transação de Foco (19/08).
 *
 * As guardas, e o estado real que cada uma cobre:
 *  · **sessão ausente → `wait`.** Num cold start a URL chega antes de a
 *    recuperação assentar. Descartar ali perderia exatamente o toque que abriu
 *    o app.
 *  · **sessão diferente da do card → `discard`.** Card obsoleto (sessão
 *    anterior que o iOS demorou a dispensar) não pode agir sobre a sessão de
 *    agora. Consome, porque o pedido já não vale para ninguém.
 *  · **já no estado pedido → `discard`.** O card pode estar mostrando estado
 *    velho, e pausar o que já está pausado reabriria a activity à toa.
 */
export function resolveFocusAction(
  request: FocusDeepLinkCommand | null,
  session: { id: string; paused: boolean } | null,
): FocusActionOutcome {
  if (!request) return { kind: "discard" };
  if (!session) return { kind: "wait" };
  if (request.sessionId && request.sessionId !== session.id) return { kind: "discard" };

  if (request.action === "pause") {
    return session.paused ? { kind: "discard" } : { kind: "apply", action: "pause" };
  }
  if (request.action === "resume") {
    return session.paused ? { kind: "apply", action: "resume" } : { kind: "discard" };
  }
  // Encerrar vale nos dois estados: sessão pausada também termina.
  return { kind: "apply", action: "end" };
}

/* ── Dedupe da reentrega nativa (BUG-232) ────────────────────────────────── */

/** O último comando APLICADO, por qualquer porta — persistido pelo lado nativo do módulo. */
export interface AppliedFocusCommand {
  url: string;
  /** Instante do comando: a chegada (porta normal) ou a captura no Swift (replay). */
  atMs: number;
}

/**
 * Folga entre o instante da captura no Swift (o `id` do replay) e o da chegada
 * pelo `appUrlOpen` (o que a porta normal grava). A captura acontece ANTES de
 * o bridge entregar, então o `id` é sempre anterior; a folga cobre jitter de
 * relógio, nunca um toque novo — dois toques no mesmo verbo exigem retomar no
 * meio, o que leva segundos.
 */
export const FOCUS_COMMAND_REPLAY_TOLERANCE_MS = 2_000;

/**
 * O replay é uma DUPLICATA do que já foi aplicado?
 *
 * O `AppDelegate` guarda toda URL `livra://foco` e só a apaga quando o replay
 * confirma ou quando envelhece (60 s) — ele não tem como saber que o caminho
 * normal já entregou. Numa recriação da WebView dentro desses 60 s o mesmo
 * `pausar` voltava, e a dedupe por `id` morria junto com o processo: a sessão
 * que a pessoa já tinha retomado pela tela era pausada de novo.
 *
 * Duplicata = mesma URL e `id` (instante da captura) não POSTERIOR ao último
 * aplicado. Um toque novo no mesmo verbo tem captura posterior por segundos.
 */
export function isReplayDuplicate(
  last: AppliedFocusCommand | null,
  url: string,
  idMs: number,
  toleranceMs: number = FOCUS_COMMAND_REPLAY_TOLERANCE_MS,
): boolean {
  if (!last || !Number.isFinite(idMs)) return false;
  if (last.url !== url) return false;
  return idMs <= last.atMs + toleranceMs;
}

export function parseFocusDeepLink(url: string): FocusDeepLinkCommand | null {
  const m = /^livra:\/\/foco\/([a-z]+)(?:\?(.*))?$/i.exec(url.trim());
  if (!m) return null;

  const action = VERBOS[m[1].toLowerCase()];
  if (!action) return null;

  let sessionId: string | null = null;
  if (m[2]) {
    for (const parte of m[2].split("&")) {
      const [chave, valor] = parte.split("=");
      if (chave === "s" && valor) {
        // `decodeURIComponent` LANÇA em sequência malformada (`%E0%A4%A`), e o
        // throw subia pelo listener de `appUrlOpen` e pela global do replay —
        // que o nativo então retentava 7× (BUG-234). Link ilegível não é nosso.
        try {
          sessionId = decodeURIComponent(valor);
        } catch {
          return null;
        }
      }
    }
  }
  return { action, sessionId };
}
